// src/features/explore/PersonCard.tsx — one person row/card with a Follow button.
// Shared by PeopleResults (browse) and SearchResults (server search, 1.24.00),
// so the two surfaces cannot drift. `bio` is optional: the suggested-profiles
// payload has none, the search payload adds it.
import styled from 'styled-components';
import { ConsolePanel, Avatar, Button, MetaRow } from '@/ui';
import { useFollow, useRequireAuth, followButtonLabel } from '@/hooks';
import { formatCount } from '@/components/formatCount';
import type { SuggestedProfile } from '@/core/models/account';

const Card = styled(ConsolePanel)`display: flex; align-items: center; gap: 10px;`;
const Info = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px;`;
const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600; color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const Sub = styled.span`
  font-family: ${({ theme }) => theme.font.mono}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Bio = styled.span`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

export interface PersonCardProps {
  profile: SuggestedProfile & { bio?: string };
}

export function PersonCard({ profile }: PersonCardProps) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(profile.handle);
  const requireAuth = useRequireAuth();
  function handleFollow() {
    if (!requireAuth('follow')) return;
    void toggleFollow();
  }
  return (
    <Card>
      <Avatar name={profile.name} size={40} avatarKey={profile.av} />
      <Info>
        <Name>{profile.name}</Name>
        <Sub>
          <MetaRow>
            <span>{profile.handle}</span>
            {profile.followerCount > 0 && (
              <span>
                {`${formatCount(profile.followerCount)} ${profile.followerCount === 1 ? 'follower' : 'followers'}`}
              </span>
            )}
          </MetaRow>
        </Sub>
        {profile.bio && <Bio title={profile.bio}>{profile.bio}</Bio>}
      </Info>
      <Button
        variant={isFollowing || isRequested ? 'secondary' : 'outline'}
        onClick={handleFollow}
        aria-label={`${followButtonLabel(isFollowing, isRequested)} ${profile.handle}`}
      >
        {followButtonLabel(isFollowing, isRequested)}
      </Button>
    </Card>
  );
}
